import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/navigation/ProtectedRoute";
import ScrollToTop from "components/ScrollToTop";
import AdminDashboard from "pages/admin-dashboard";
import AgentDashboard from "pages/agent-dashboard";
import AIAssistantPage from "pages/ai-assistant";
import AnalyticsDashboard from "pages/analytics-dashboard";
import AnnouncementManagement from "pages/announcement-management";
import ClientMessagingCenter from "pages/client-messaging-center";
import HelpPage from "pages/help";
import Home from "pages/home";
import LeaseManagement from "pages/lease-management";
import Login from "pages/login";
import MaintenanceManagement from "pages/maintenance-management";
import MessagesPage from "pages/messages";
import NotFound from "pages/NotFound";
import OrganizationManagement from "pages/organization-management";
import PasswordResetRequestPage from "pages/password-reset";
import PasswordResetConfirmPage from "pages/password-reset-confirm";
import PaymentAccountsManagement from "pages/payment-accounts-management";
import PaymentManagement from "pages/payment-management";
import PaymentProofManagement from "pages/payment-proof-management";
import PaymentProofUploadPage from "pages/payment-proof-upload";
import ProfilePage from "pages/profile";
import PropertiesPage from "pages/properties";
import PropertyDetailPage from "pages/property-detail";
import PropertyManagement from "pages/property-management";
import PropertyRequestsManagement from "pages/property-requests-management";
import PropertyViewingManagement from "pages/property-viewing-management";
import PublicHomePage from "pages/public-home";
import Register from "pages/register";
import SalesManagement from "pages/sales-management";
import SettingsPage from "pages/settings";
import TechnicianDashboard from "pages/technician-dashboard";
import TenantDashboard from "pages/tenant-dashboard";
import TenantManagement from "pages/tenant-management";
import TenantPortal from "pages/tenant-portal";
import TenancyAgreementReview from "pages/tenant-portal/components/TenancyAgreementReview";
import UserManagementPage from "pages/user-management";
import React from "react";
import {
	BrowserRouter,
	Navigate,
	Route,
	Routes as RouterRoutes,
} from "react-router-dom";

const Routes: React.FC = () => {
	return (
		<BrowserRouter>
			<ErrorBoundary>
				<ScrollToTop />
				<RouterRoutes>
					{/* Public Routes */}
					<Route path="/" element={<PublicHomePage />} />
					<Route path="/properties" element={<PropertiesPage />} />
					<Route path="/properties/:id" element={<PropertyDetailPage />} />

					{/* Auth Routes */}
					<Route path="/login" element={<Login />} />
					<Route path="/register" element={<Register />} />
					<Route
						path="/password-reset"
						element={<PasswordResetRequestPage />}
					/>
					<Route
						path="/password-reset-confirm"
						element={<PasswordResetConfirmPage />}
					/>

					{/* Dashboard Selection (old home page) */}
					<Route path="/dashboard-select" element={<Home />} />

					{/* Admin Protected Routes */}
					<Route
						path="/admin-dashboard"
						element={
							<ProtectedRoute allowedRoles={["admin"]}>
								<AdminDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/tenant-management"
						element={
							<ProtectedRoute allowedRoles={["admin"]}>
								<TenantManagement />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/property-management"
						element={
							<ProtectedRoute allowedRoles={["admin"]}>
								<PropertyManagement />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/lease-management"
						element={
							<ProtectedRoute allowedRoles={["admin"]}>
								<LeaseManagement />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/payment-management"
						element={
							<ProtectedRoute allowedRoles={["admin"]}>
								<PaymentManagement />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/maintenance-management"
						element={
							<ProtectedRoute allowedRoles={["admin"]}>
								<MaintenanceManagement />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/sales-management"
						element={
							<ProtectedRoute allowedRoles={["admin", "agent"]}>
								<SalesManagement />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/property-viewing-management"
						element={
							<ProtectedRoute allowedRoles={["admin", "agent"]}>
								<PropertyViewingManagement />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/client-messaging-center"
						element={
							<ProtectedRoute allowedRoles={["admin"]}>
								<ClientMessagingCenter />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/user-management"
						element={
							<ProtectedRoute allowedRoles={["admin"]}>
								<UserManagementPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/analytics-dashboard"
						element={
							<ProtectedRoute allowedRoles={["admin"]}>
								<AnalyticsDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/organization-management"
						element={
							<ProtectedRoute allowedRoles={["admin"]}>
								<OrganizationManagement />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/announcement-management"
						element={
							<ProtectedRoute allowedRoles={["admin"]}>
								<AnnouncementManagement />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/property-requests-management"
						element={
							<ProtectedRoute allowedRoles={["admin"]}>
								<PropertyRequestsManagement />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/payment-accounts-management"
						element={
							<ProtectedRoute allowedRoles={["admin"]}>
								<PaymentAccountsManagement />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/payment-proof-management"
						element={
							<ProtectedRoute allowedRoles={["admin"]}>
								<PaymentProofManagement />
							</ProtectedRoute>
						}
					/>

					{/* Tenant Protected Routes */}
					<Route
						path="/tenant-dashboard"
						element={
							<ProtectedRoute allowedRoles={["tenant"]}>
								<TenantDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/tenant-portal"
						element={
							<ProtectedRoute allowedRoles={["tenant"]}>
								<TenantPortal />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/tenancy-agreement/:id"
						element={
							<ProtectedRoute allowedRoles={["tenant"]}>
								<TenancyAgreementReview />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/payment-proof-upload/:requestId"
						element={
							<ProtectedRoute allowedRoles={["tenant", "admin", "agent", "technician"]}>
								<PaymentProofUploadPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/payment-proof-upload"
						element={
							<ProtectedRoute allowedRoles={["tenant", "admin", "agent", "technician"]}>
								<PaymentProofUploadPage />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/tenant-payments"
						element={
							<ProtectedRoute allowedRoles={["tenant"]}>
								<Navigate to="/tenant-dashboard#payments" replace />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/tenant-maintenance"
						element={
							<ProtectedRoute allowedRoles={["tenant"]}>
								<Navigate to="/tenant-dashboard#maintenance" replace />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/tenant-messages"
						element={
							<ProtectedRoute allowedRoles={["tenant"]}>
								<Navigate to="/messages" replace />
							</ProtectedRoute>
						}
					/>

					{/* Technician Protected Routes */}
					<Route
						path="/technician-dashboard"
						element={
							<ProtectedRoute allowedRoles={["technician"]}>
								<TechnicianDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/technician-tasks"
						element={
							<ProtectedRoute allowedRoles={["technician"]}>
								<Navigate to="/technician-dashboard" replace />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/technician-messages"
						element={
							<ProtectedRoute allowedRoles={["technician"]}>
								<Navigate to="/messages" replace />
							</ProtectedRoute>
						}
					/>

					{/* Agent Protected Routes */}
					<Route
						path="/agent-dashboard"
						element={
							<ProtectedRoute allowedRoles={["agent"]}>
								<AgentDashboard />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/agent-properties"
						element={
							<ProtectedRoute allowedRoles={["agent"]}>
								<Navigate to="/agent-dashboard" replace />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/agent-viewings"
						element={
							<ProtectedRoute allowedRoles={["agent"]}>
								<Navigate to="/agent-dashboard" replace />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/agent-messages"
						element={
							<ProtectedRoute allowedRoles={["agent"]}>
								<Navigate to="/messages" replace />
							</ProtectedRoute>
						}
					/>

					{/* Profile Route - All authenticated users */}
					<Route
						path="/profile"
						element={
							<ProtectedRoute
								allowedRoles={["admin", "tenant", "technician", "agent"]}
							>
								<ProfilePage />
							</ProtectedRoute>
						}
					/>

					{/* Settings Route - All authenticated users */}
					<Route
						path="/settings"
						element={
							<ProtectedRoute
								allowedRoles={["admin", "tenant", "technician", "agent"]}
							>
								<SettingsPage />
							</ProtectedRoute>
						}
					/>

					{/* Help Route - All authenticated users */}
					<Route
						path="/help"
						element={
							<ProtectedRoute
								allowedRoles={["admin", "tenant", "technician", "agent"]}
							>
								<HelpPage />
							</ProtectedRoute>
						}
					/>

					{/* Messages Route - All authenticated users */}
					<Route
						path="/messages"
						element={
							<ProtectedRoute
								allowedRoles={["admin", "tenant", "technician", "agent"]}
							>
								<MessagesPage />
							</ProtectedRoute>
						}
					/>

					{/* AI Assistant Route - All authenticated users */}
					<Route
						path="/ai-assistant"
						element={
							<ProtectedRoute
								allowedRoles={["admin", "tenant", "technician", "agent"]}
							>
								<AIAssistantPage />
							</ProtectedRoute>
						}
					/>

					{/* 404 */}
					<Route path="*" element={<NotFound />} />
				</RouterRoutes>
			</ErrorBoundary>
		</BrowserRouter>
	);
};

export default Routes;
